/* ============================================================================
   CAPRIMAFFIA - HOME TOP - 01 ESCAPARATE EN ANILLO
   Un anillo de fichas de producto en 3D.

   -------------------------------------------------------------------- v3
   Revision del 3 oct 2026. Diego cambia COMO SE MANEJA el anillo (decision
   suya, no se re-litiga):

     1) De lejos (la vista desde arriba, antes del acercamiento) el anillo se
        GIRA arrastrando de lado a lado: raton, trackpad horizontal (deltaX de
        la rueda) y dedo. Con inercia al soltar y encaje final a una ficha,
        nunca a medias.
     2) Una vez hecho el acercamiento, EL SCROLL VERTICAL YA NO CAMBIA DE
        PRODUCTO. Bajar = pasar a la siguiente seccion. El scroll vertical solo
        gobierna el acercamiento (y su inverso al subir), que sigue siendo una
        animacion continua ligada al scroll.
     3) Los productos se cambian SOLO con gesto horizontal, pinchando en una
        ficha lateral, con el cuadrado de la barra -que ahora gira el anillo
        DIRECTAMENTE, ya no mueve el scroll de la pagina- y con el teclado.

   Lo que eso cambia por dentro, respecto a la v2:

     A) DOS FUENTES DE MOVIMIENTO, INDEPENDIENTES Y SIN SOLAPE.
        - `S.f` (0..1) es el acercamiento. Lo arrastra UN ScrollTrigger con
          `scrub` sobre una timeline de UN solo tramo. Igual de continuo y
          reversible que en la v2, pero el pin ya no mide 4.360 px sino
          `zoom + hold` = 1.000 px por defecto.
        - `P.pos` es la posicion del anillo en fichas. Ya NO la toca el scroll:
          la mueven los gestos, y el encaje es un tween de duracion fija.
        En la v2 las dos cosas eran el mismo numero (el progreso del scroll) y
        por eso no se podian separar.

     B) SE QUITA EL `snap` DEL ScrollTrigger. Era lo unico que aun escribia el
        scroll del usuario. Queda un ajuste para volver a encenderlo
        («Encajar el acercamiento»), apagado por defecto: con el pin tan corto
        no hace falta y es justo lo que daba la sensacion de estar atrapado.

     C) EL RECORRIDO PASA A SER CICLICO. Al no depender del scroll, `P.pos` ya
        no esta encerrada en [0, N]: el anillo gira infinito en los dos
        sentidos. Eso elimina de raiz la trampa de la v2 (recorrido LINEAL
        sobre un anillo CERRADO: cualquier destino habia que envolverlo o
        `goCard` lo recortaba y el clic en las laterales del extremo no hacia
        nada). Ahora el destino es siempre `posicion actual +- camino corto` y
        no hay extremos. Para que el angulo no crezca sin limite en una sesion
        larga, al quedarse quieto se reduce modulo N: 360 grados de mas se ven
        exactamente igual.

     D) EL SCROLL VERTICAL NO SE BLOQUEA NUNCA. En el dedo lo separa
        `touch-action: pan-y` mas un umbral de direccion (8 px y |dx| > 1,2|dy|);
        en trackpad, `deltaX` gira y `deltaY` se deja pasar sin tocarlo.

   Lo que NO cambia (y que hay que seguir defendiendo):
     - La nitidez de la ficha de frente. El 3D es del ANILLO, no de las fichas:
       el aro va en translateZ(-R) y cada hueco en translateZ(+R), asi que la
       de frente compone la IDENTIDAD -> escala 1 exacta, sin fraccion ni blur.
       Para eso `camz`/`tilt`/`camy` tienen que caer en 0 CLAVADO: aqui se
       fuerza (f >= 0.9995 se trata como 1 exacto) en vez de dejarselo al
       easing. Y `will-change` solo mientras algo se mueve.
     - `opacity` PROHIBIDA en el hueco del anillo: es propiedad de agrupacion,
       fuerza `transform-style: flat` aunque pongas `preserve-3d`, y el desgiro
       de la ficha se aplana contra el plano girado del hueco -> trapecios.
       La profundidad va con escala + velo sobre ficha opaca.
     - Chrome NO hace hit-test de las fichas laterales (estan en un
       `preserve-3d` anidado y `elementFromPoint` devuelve siempre `.ha__cam`).
       El clic en una lateral y el cursor de mano van por `slotAt()`.
     - Cero listeners de `scroll` propios. Cero lecturas de layout por
       fotograma.

   -------------------------------------------------------------------- v4
   Revision del 8 oct 2026: FLUIDEZ. Diego: «que sea mega fluido, que no de
   tirones». Medido con traza de Chrome (GPU real) antes de tocar nada:

     A) EL RECALCULO DE ESTILOS SE COMIA EL FOTOGRAMA. La camara (--ha-camz,
        --ha-tilt, --ha-camy) se escribia en la SECCION y el giro (--ha-rot)
        en el ARO, como variables CSS. Las variables se HEREDAN: cada cambio
        invalidaba el estilo de todo lo que cuelga debajo -20 fichas con su
        foto, textos, tallas y botones-. Medido en 6 giros: 2.573 ms de
        recalculo de estilo en 4,5 s (~10 ms por fotograma, de 16 que hay).
        Ahora el JS escribe el `transform` YA HECHO en el elemento que se
        mueve (camara, aro, cada ficha visible) y el velo como `opacity` de su
        propia capa. Nada heredable cambia por fotograma.
     B) EL ACERCAMIENTO ARRANCABA A GOLPES. El `scrub` de GSAP persigue el
        scroll con un tween que sale a toda velocidad: con la rueda, cada golpe
        es un salto de velocidad (medido: hasta 0,35 del acercamiento en un
        fotograma). Ahora lo lleva un MUELLE CRITICAMENTE AMORTIGUADO
        (solucion exacta, no Euler): la velocidad es continua, nunca pega
        arrancones ni rebota, y termina en el valor EXACTO (nitidez).
     C) ARRANCAR Y PARAR DABAN UN TIRON. Al empezar y al acabar cada
        movimiento se ponia/quitaba una clase (`is-still`, `is-rest`) en la
        SECCION, y una clase en la seccion obliga a recalcular las 20 fichas
        enteras: en movil, 80-100 ms justo al arrancar y al encajar cada giro.
        Ahora el `will-change` se escribe en linea en las tres capas que lo
        llevan (camara, aro, cuadrado) y la ficha de frente se aplana sola;
        al cambiar de ficha de frente solo se tocan la que sale y la que entra.
     D) CAMBIAR DE FICHA RECALCULABA TODA LA PAGINA. El tema (filtros,
        variantes, menus, galerias) tiene decenas de reglas `:has()` que miran
        `:disabled`, `[aria-hidden]` o si un elemento esta vacio. Con ellas,
        cambiar UN `disabled`, UN `aria-hidden` o UN texto con `textContent`
        en cualquier sitio obliga a Chrome a revalidar el <body> entero
        (1.846 elementos): medido, 14-21 ms por cambio en PC, ~100 ms en un
        movil medio. `setFront` hacia varios en cada cambio de ficha de frente
        -o sea, a mitad de cada giro-. Ahora, durante el movimiento, solo se
        cambian la clase `is-front`, los `tabindex` y el numero (por `.data`
        del nodo de texto, que no cuenta como cambio de contenido); el aviso
        para lectores de pantalla se da AL PARARSE, el `aria-hidden` de las
        fichas se ha QUITADO del todo (costaba el ultimo recalculo del body
        que quedaba, 180 ms con CPU x4) y el `disabled` del boton «girar hasta
        aqui» ya no se toca (en la de frente ya lo oculta el CSS).
     E) LAS FOTOS DEL ARO van `lazy` y en la vista de lejos se ven las 20: al
        quedar la pagina en reposo se piden todas, para que ninguna aparezca
        de golpe al llegar.

   Sin GSAP, sin soporte 3D o con prefers-reduced-motion no se monta nada: la
   seccion se queda como la pinta el CSS, un carrusel lineal con snap nativo.
   Produccion 199435485529 no se toca.
   ========================================================================== */
(function () {
  'use strict';

  var RM = window.matchMedia('(prefers-reduced-motion: reduce)');
  var instances = [];

  var clamp = function (v, a, b) { return v < a ? a : (v > b ? b : v); };
  var num = function (el, attr, def) {
    var v = parseFloat(el.getAttribute(attr));
    return isNaN(v) ? def : v;
  };
  var FOCUSABLE = 'a[href],button,input,select,textarea,[tabindex]';
  /* Px antes de decidir si el gesto es horizontal. Con el dedo hace falta
     margen para no robarle el scroll vertical al navegador; con el raton no
     hay ambiguedad, asi que el umbral es mas corto y el anillo responde antes. */
  var UMBRAL_DEDO = 8;
  var UMBRAL_RATON = 4;

  function can3d() {
    if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') return false;
    if (RM.matches) return false;
    return !!(window.CSS && CSS.supports && CSS.supports('transform-style', 'preserve-3d'));
  }

  /* ========================================================================
     Una instancia por seccion.
     ===================================================================== */
  function Anillo(sec) {
    var stage = sec.querySelector('.ha__stage');
    var ring = sec.querySelector('.ha__ring');
    var cam = sec.querySelector('.ha__cam');
    var slots = Array.prototype.slice.call(sec.querySelectorAll('.ha__slot'));
    var rail = sec.querySelector('.ha__rail');
    var sq = sec.querySelector('.ha__sq');
    var live = sec.querySelector('.ha__live');
    var posEl = sec.querySelector('.ha__pos');
    var N = slots.length;
    if (!stage || !ring || !cam || N < 3) return null;

    /* ---------------------------------------------------- ajustes del editor */
    var CFG = {
      side:    Math.round(num(sec, 'data-side', 3)),
      sideM:   Math.round(num(sec, 'data-side-m', 1)),
      draw:    Math.round(num(sec, 'data-draw', 2)),     // margen de dibujo
      radius:  num(sec, 'data-radius', 0),               // 0 = automatico
      radiusM: num(sec, 'data-radius-m', 0),
      radiusK: num(sec, 'data-radius-k', 98) / 100,
      angle:   num(sec, 'data-angle', 0),                // 0 = 360/N
      tilt:    num(sec, 'data-tilt', 26),
      far:     num(sec, 'data-far', 1600),
      farM:    num(sec, 'data-far-m', 1600),
      camy:    num(sec, 'data-camy', 190),
      zoom:    Math.max(120, num(sec, 'data-zoom', 760)),
      hold:    Math.max(0, num(sec, 'data-hold', 240)),
      zoomSnap: sec.getAttribute('data-zoom-snap') === '1',
      scrub:   clamp(num(sec, 'data-scrub', 45), 0, 150) / 100,
      zoomEase: sec.getAttribute('data-zoom-ease') || 'power2.inOut',
      turnEase: sec.getAttribute('data-turn-ease') || 'power2.inOut',
      turnDur: Math.max(120, num(sec, 'data-turn-dur', 560)) / 1000,
      snapMax: Math.max(80, num(sec, 'data-dur', 520)) / 1000,
      snapMin: Math.max(60, num(sec, 'data-snap-min', 220)) / 1000,
      snapEase: sec.getAttribute('data-ease') || 'power2.inOut',
      veil:    clamp(num(sec, 'data-side-veil', 14), 0, 85) / 100,
      sideSc:  clamp(num(sec, 'data-side-sc', 92), 50, 100) / 100,
      dragPx:  Math.max(24, num(sec, 'data-drag-px', 150)),
      swipe:   Math.max(24, num(sec, 'data-swipe', 110)),
      wheelK:  clamp(num(sec, 'data-wheel-k', 100), 10, 400) / 100,
      inertia: clamp(num(sec, 'data-inertia', 100), 0, 300) / 100,
      gap:     num(sec, 'data-gap', 10),
      pinDy:   Math.round(num(sec, 'data-pin-dy', 0))
    };
    /* Ninguna ficha de espaldas: pasados 78 grados ya se ve de canto. Con
       pocas fichas el paso angular es grande, asi que se destacan menos a los
       lados (con 10 fichas el paso es 36 grados: solo caben 2 por lado).    */
    var STEP_DEG = CFG.angle > 0 ? CFG.angle : 360 / N;
    var SIDE_MAX = Math.max(1, Math.min(Math.floor(78 / STEP_DEG), Math.floor((N - 1) / 2)));

    var mq = window.matchMedia('(max-width: 749px)');
    var isM = function () { return mq.matches; };
    var perSide = function () { return Math.min(isM() ? CFG.sideM : CFG.side, SIDE_MAX); };

    /* ---------------------------------------------------------------- MOVIL
       8 oct 2026. Diego, en su iPhone, con la v4 ya subida: «en PC va
       increible, pero el movil se lo ha cargado» -los banners y el anillo se
       superponen, el anillo sale cortado y al final del acercamiento da un
       salto y hay que bajar mas-. No se habia visto porque los bancos de la v4
       emulaban 390x844 con Chrome de escritorio y movian la pagina con
       `mouse.wheel`: eso no tiene ni inercia de compositor ni gesto tactil.
       Midiendo con dedo de verdad (`Input.dispatchTouchEvent` con marcas de
       tiempo, que es lo unico que hace que Blink lance el fling) salieron dos
       culpables, los dos nuevos de la v4, y los dos SOLO danan en tactil.
       Todo lo de aqui abajo cuelga de `tactil()`: en PC no cambia nada.      */
    var MQC = window.matchMedia('(pointer: coarse)');
    var tactil = function () { return mq.matches || MQC.matches; };


    /* ------------------------------------------- el pin: SOLO el acercamiento
       Antes el pin media `zoom + N * step` (760 + 20 x 180 = 4.360 px) porque
       el scroll giraba el anillo. Ahora mide `zoom + hold` = 1.000 px: el
       acercamiento y un rato corto ya cerca, para que girar de lado a lado sea
       lo natural. Pasado eso, bajar es pasar a la siguiente seccion.
       Se mantiene el pin (en vez de ligar el acercamiento a la entrada de la
       seccion) por tres razones: el acercamiento tiene que ser continuo y
       reversible con el scroll -y eso es exactamente lo que da un
       ScrollTrigger con scrub-; sin pin la seccion seguiria subiendo y el
       anillo se iria de la pantalla justo cuando se quiere girar; y el anillo
       tiene que quedarse centrado y quieto mientras se maneja.              */
    var TOT = CFG.zoom + CFG.hold;
    var ZF = CFG.zoom / TOT;

    /* --------------------------------------------------------------- estado */
    var S = { f: 0 };        // acercamiento, lo mueve el scroll
    var P = { pos: 0 };      // posicion del anillo en fichas, la mueven los gestos
    var tl = null, st = null, rotTw = null;
    var R = 900, cardW = 0, railW = 0, sqW = 10, FAR = 1600;
    var front = -1, lastRot = null, lastF = null, lastSqX = null;
    var drag = null, railDrag = false, railBox = null, didDrag = false, railCur = false;
    var wT = null, wOn = false, wPos0 = 0;
    var still = false, rest = false, stillT = null, lastMove = 0;
    var hidCache = new Array(N);
    var flatK = 1, lastCam = null, lastRing = null;
    var roH = 0, roT = null, ro = null, refreshT = null;

    /* los enfocables de cada ficha se cachean: en la v1 se volvia a consultar
       el DOM de las 20 fichas en cada cambio de ficha de frente */
    var meta = slots.map(function (s, i) {
      var card = s.querySelector('.ha-card');
      s.style.setProperty('--ha-a', (i * STEP_DEG).toFixed(4) + 'deg');
      return {
        slot: s,
        card: card,
        veil: s.querySelector('.ha-card__veil'),
        a: +(i * STEP_DEG).toFixed(4),
        tf: null,                  // ultimo transform escrito (cache)
        vo: null,                  // ultima opacidad de velo escrita
        turn: s.querySelector('.ha-turn'),
        foc: card ? Array.prototype.slice.call(card.querySelectorAll(FOCUSABLE)) : [],
        name: s.getAttribute('data-name') || ''
      };
    });

    /* ------------------------------------------------------------- medidas
       Todo en enteros: medio pixel en el ancho o en el left es medio pixel de
       desenfoque en la ficha de frente. offsetWidth/offsetHeight dan la caja
       de layout (sin transformar), que es justo lo que hace falta.
       Se llama SOLO en el montaje y en `onRefreshInit` (antes de que
       ScrollTrigger mida el pin), nunca por fotograma.                      */
    function measure() {
      /* 🚨 Las LECTURAS van todas juntas y DESPUES las escrituras. Antes se
         alternaban (leer ancho -> escribir --ha-cw -> leer altos -> escribir
         --ha-buy-h -> leer altos -> escribir) y eso fuerza un calculo de layout
         por cada cambio de turno: Lighthouse lo medio en 32 ms de reflujo
         forzado en el movil. Ahora son DOS pasadas, el minimo posible: el alto
         de la ficha depende de `--ha-buy-h`, asi que hay que escribirla antes
         de poder medirlo.                                                     */
      sec.style.removeProperty('--ha-cw');
      sec.style.removeProperty('--ha-sh');
      sec.style.removeProperty('--ha-buy-h');

      // ---- pasada 1 de lecturas (nada de escribir en medio)
      cardW = slots[0].offsetWidth || 300;
      var buy = 0;
      for (var i = 0; i < N; i++) {
        var bq = slots[i].querySelector('.ha-card__buy');
        if (bq) buy = Math.max(buy, bq.offsetHeight);
      }
      var stageW = stage.clientWidth;
      var persp = parseFloat(getComputedStyle(stage).perspective);
      var fk = parseFloat(getComputedStyle(sec).getPropertyValue('--ha-flat-k'));
      flatK = isNaN(fk) ? 1 : fk;
      if (rail && sq) {
        sqW = sq.offsetWidth || 10;
        railW = Math.max(0, rail.clientWidth - sqW);
      }

      /* 🚨 PARIDAD: el centro de la ficha TIENE que caer en el eje del aro.
         El hueco se coloca con `left` ENTERO (medio pixel de left es medio
         pixel de desenfoque), asi que su centro queda en `left + cardW / 2`
         y el eje del aro esta en `stageW / 2`. Si `stageW - cardW` es impar,
         ese centro se queda a MEDIO PIXEL del eje, y entonces al girar el aro
         la ficha de frente ya no mide su ancho exacto: la perspectiva la
         escala un pelin distinto en cada ficha. Medido en movil 390 x 281:
         281,14 px en la ficha 06 y 280,86 px en la 16, con solo la 01 y la 11
         clavadas. En 1366 (ficha 314) y en 1920 (ficha 340) salia par de
         casualidad, y por eso ahi la ficha de frente ya media exacto.
         Arreglo: quitarle 1 px al ancho de la ficha para igualar la paridad.
         Un pixel no se ve; medio pixel de desenfoque, si.                    */
      if (Math.abs(((stageW - cardW) / 2) - Math.round((stageW - cardW) / 2)) > 0.002
          && cardW > 60) {
        cardW -= 1;
      }

      // ---- escrituras que hacen falta para poder medir el alto
      sec.style.setProperty('--ha-cw', cardW + 'px');
      // misma altura de bloque de compra en todas: el aro no debe bailar
      if (buy > 0) sec.style.setProperty('--ha-buy-h', Math.ceil(buy) + 'px');

      // ---- pasada 2 de lecturas
      var h = 0;
      for (var j = 0; j < N; j++) h = Math.max(h, slots[j].offsetHeight);
      h = Math.ceil(h);

      // ---- resto de escrituras
      sec.style.setProperty('--ha-sh', h + 'px');
      roH = h;                       // referencia del ResizeObserver: este alto
                                     // lo acabamos de poner nosotros, no cuenta

      var manual = isM() ? CFG.radiusM : CFG.radius;
      if (manual > 0) {
        R = Math.round(manual);
      } else {
        // radio en el que dos fichas vecinas se tocan justo (cuerda del aro),
        // por el factor que elija Diego. Vale igual con 6 fichas que con 30.
        R = Math.round((cardW + CFG.gap) / (2 * Math.sin(Math.PI / N)) * CFG.radiusK);
      }
      R = Math.max(cardW, R);
      sec.style.setProperty('--ha-R', R + 'px');
      sec.style.setProperty('--ha-slot-left', Math.round((stageW - cardW) / 2) + 'px');

      FAR = farFor(persp, stageW);

      lastRot = null; lastF = null; lastSqX = null; lastCam = null; lastRing = null;
      hidCache = new Array(N);
      for (var q = 0; q < N; q++) { meta[q].tf = null; meta[q].vo = null; }
    }

    /* ------------------------------- a que distancia se ve el anillo al llegar
       En PC manda «Distancia de la vista inicial» y con 20 fichas entra el aro
       entero. En el movil no: la pantalla es estrecha y con esa misma distancia
       solo caben 12 de 20 (medido en 390x844). Por eso el movil tiene su propio
       ajuste, igual que ya tenia el radio y la perspectiva:
         > 0  la distancia que diga Diego (por defecto, la misma que en PC);
         = 0  automatica: la justa para que el anillo entero quepa de ancho.
       La cuenta es la de la perspectiva: un punto al borde del aro
       (x = R + media ficha) a la profundidad del aro (z = -(far + R)) se
       proyecta en x * persp / (persp + far + R). Despejando far sale lo de
       abajo. Nunca se queda por debajo de la distancia de PC.               */
    function farFor(persp, stageW) {
      if (!isM()) return CFG.far;
      if (CFG.farM > 0) return CFG.farM;
      if (!persp || persp <= 0) return CFG.far;
      var media = stageW / 2 - 10;
      if (media <= 0) return CFG.far;
      var borde = R + cardW / 2;
      return Math.max(CFG.far, Math.round(borde * persp / media - persp - R));
    }

    /* --------------------------------------------------------------- pintar
       Convierte los dos numeros del estado (S.f y P.pos) en transformaciones.
       Una sola pasada: camara, aro, velos, escalas, ficha de frente y cuadrado
       de la barra. Nada de leer el DOM.                                     */
    var wcOn = null;
    function setWC(on) {
      if (on === wcOn) return;
      wcOn = on;
      var v = on ? 'transform' : 'auto';
      cam.style.willChange = v;
      ring.style.willChange = v;
      if (sq) sq.style.willChange = v;
    }

    function paint() {
      lastMove = performance.now();
      if (still) {
        still = false;
        rest = false;
      }
      setWC(true);
      if (!stillT) stillT = setTimeout(checkQuiet, 190);

      /* La nitidez depende de que la camara acabe en la IDENTIDAD exacta, asi
         que el final no se le deja al easing: a partir de 0,9995 se trata como
         1 clavado y las tres variables se escriben en 0 exacto. Con far = 1600,
         un 0,04 % de escala residual en la ficha de frente ya se ve.        */
      var f = S.f >= 0.9995 ? 1 : (S.f <= 0.0005 ? 0 : S.f);
      var g = 1 - f;
      /* 🚨 v4: el transform se escribe HECHO en la camara, no como variables
         en la seccion (que heredan las 20 fichas y obligan a recalcularlas
         todas). Mismo orden y mismos valores que la regla del CSS. */
      var ct = g === 0
        ? 'translateZ(0px) rotateX(0deg) translateY(0px)'
        : 'translateZ(' + (-FAR * g).toFixed(1) + 'px) rotateX(' + (-CFG.tilt * g).toFixed(3)
          + 'deg) translateY(' + (CFG.camy * g).toFixed(1) + 'px)';
      if (ct !== lastCam) { lastCam = ct; cam.style.transform = ct; }

      var pos = P.pos;
      var rotN = +(-pos * STEP_DEG).toFixed(3);
      var rt = 'translateZ(' + (-R) + 'px) rotateY(' + rotN + 'deg)';
      if (rt !== lastRing) { lastRing = rt; ring.style.transform = rt; }

      var side = perSide();
      var drawTo = side + CFG.draw;
      var llegada = f > 0.985;            // la camara ya esta encima
      for (var i = 0; i < N; i++) {
        var m = meta[i];
        var off = i - pos;
        while (off > N / 2) off -= N;
        while (off < -N / 2) off += N;
        var a = off < 0 ? -off : off;

        /* Apagar con `visibility`, no con opacidad, y SOLO cuando la camara ha
           llegado: ahi la ficha esta entera por detras de las de delante (que
           son opacas), asi que encenderla o apagarla no se ve. Mientras la
           camara esta lejos no se apaga ninguna, que es cuando se quiere ver
           el anillo completo desde arriba.                                   */
        var hid = llegada && a > drawTo;
        if (hidCache[i] !== hid) {
          hidCache[i] = hid;
          m.slot.classList.toggle('is-hid', hid);
        }
        if (hid || !m.card) continue;   // tapada: no se le toca nada

        var t = side > 0 ? (a < side ? a / side : 1) : 1;
        var sc = 1 - (1 - CFG.sideSc) * t;
        /* la ficha se desgira lo que la gira el aro: siempre de cara */
        var ry = -flatK * (rotN + m.a);
        var tf = 'rotateY(' + (Math.abs(ry) < 0.0005 ? '0' : ry.toFixed(3)) + 'deg) scale('
          + (+sc.toFixed(4)) + ')';
        if (tf !== m.tf) { m.tf = tf; m.card.style.transform = tf; }

        var v = CFG.veil * t * f;       // de lejos el anillo va limpio, sin velo
        if (m.veil && (m.vo === null || Math.abs(v - m.vo) > 0.006 || (v === 0 && m.vo !== 0))) {
          m.vo = v;
          m.veil.style.opacity = v === 0 ? '0' : v.toFixed(3);
        }
      }

      var k = Math.round(pos) % N;
      if (k < 0) k += N;
      if (k !== front) setFront(k);

      if (sq && railW > 0) {
        var m = pos % N;
        if (m < 0) m += N;
        var x = Math.round((m / N) * railW);
        if (x !== lastSqX) {
          lastSqX = x;
          sq.style.setProperty('--ha-sq-x', x + 'px');
        }
      }
    }

    /* Cuidado: NO se usa inert en las laterales. inert mata tambien el clic, y
       un clic en una lateral tiene que girar el anillo hasta ella. Lo que se
       hace es sacarlas del TABULADOR (tabindex -1) y dejar encima un boton
       propio ("girar hasta aqui") que si es operable.
       🚨 v4: tampoco se les pone `aria-hidden`. Costaba un recalculo del
       <body> ENTERO cada vez que el anillo se paraba (180 ms con CPU x4),
       porque el tema tiene reglas `:has([aria-hidden=...])` y el selector
       `:has` obliga a revalidar hacia arriba. Y ademas estaba mal de serie:
       `aria-hidden` sobre una ficha que contiene botones -aunque sean
       tabindex -1- es justo lo que prohibe la especificacion. Ahora las 20
       fichas quedan legibles para el lector de pantalla (el anillo es una
       lista de productos, tiene sentido poder leerla), el tabulador solo
       entra en la de frente, y al pararse se avisa de cual manda por el
       `.data` de la region `aria-live` (0,07 ms, no invalida nada).         */
    /* nodo de texto propio para el numero y el aviso: cambiar su `.data` no
       es un cambio de contenido (ver D en la cabecera) */
    function textNode(el, ph) {
      if (!el) return null;
      if (!el.firstChild || el.firstChild.nodeType !== 3 || el.childNodes.length > 1) {
        el.textContent = el.textContent || ph || ' ';
      }
      return el.firstChild;
    }
    /* 🚨 El aviso para lectores de pantalla nace con un ESPACIO DURO, no con
       un espacio normal, y esto no es cosmetica: para `:empty` de Chrome un
       elemento con solo espacios en blanco SIGUE estando vacio. El tema
       (base.css) trae `p:empty{display:none}` y, peor, la regla sin calificar
       `:where(p,h1..h6) + :has(+ :empty:last-child)`: con ella, cualquier
       elemento puede verse afectado por `:has`, asi que el primer aviso -que
       sacaba este <p> de vacio a con texto- obligaba a revalidar el <body>
       entero. Medido: UN recalculo de 1.847 elementos (10,8 ms en PC, ~180 ms
       con CPU x4) justo al pararse el anillo por primera vez, que es el peor
       momento posible. El espacio duro NO es espacio en blanco de documento,
       asi que el parrafo ya nace lleno, nunca cambia de estado y cambiar su
       `.data` cuesta 0,07 ms. No se ve: `.ha-sr` va recortada a 1x1 px.     */
    var posTx = textNode(posEl), liveTx = textNode(live, ' ');
    var a11yFront = -1;

    function setFront(k) {
      var prev = front;
      front = k;
      for (var i = 0; i < N; i++) {
        /* solo cambian la que sale y la que entra (la primera vez, todas) */
        if (prev >= 0 && i !== prev && i !== k) continue;
        var m = meta[i], on = i === k;
        m.slot.classList.toggle('is-front', on);
        for (var j = 0; j < m.foc.length; j++) {
          if (on) m.foc[j].removeAttribute('tabindex');
          else m.foc[j].setAttribute('tabindex', '-1');
        }
      }
      if (rail) {
        rail.setAttribute('aria-valuenow', String(k + 1));
        rail.setAttribute('aria-valuetext', meta[k].name + ', ' + (k + 1) + ' de ' + N);
      }
      if (posTx) posTx.data = ('0' + (k + 1)).slice(-2);
      if (prev < 0) applyA11y();
    }

    /* Al pararse: avisar de que ficha manda. Ya no toca ningun atributo (ver
       el comentario de arriba), solo el texto de la region aria-live.       */
    function applyA11y() {
      if (a11yFront === front) return;
      var first = a11yFront < 0;
      a11yFront = front;
      if (liveTx && !first) liveTx.data = meta[front].name;
    }

    /* ------------------------------------------------- quieto = nitido
       Dos grados de quietud. `is-still` quita el will-change siempre que nada
       se mueve (el navegador vuelve a rasterizar a resolucion completa).
       `is-rest` ademas le quita el transform a la ficha de frente, y eso solo
       se puede hacer si esta encajada EXACTA: si no, se veria dar un tiron.  */
    function checkQuiet() {
      if (performance.now() - lastMove < 170) { stillT = setTimeout(checkQuiet, 90); return; }
      stillT = null;
      still = true;
      applyA11y();
      /* 🚨 v4: nada de clases en la seccion (recalculan las 20 fichas) */
      setWC(false);
      if (S.f > 0.999 && Math.abs(P.pos - Math.round(P.pos)) < 0.004) {
        rest = true;
        /* encajada y quieta: fuera el transform de la de frente (la regla
           `.is-rest` del CSS ya no alcanza, el transform va en linea) */
        var fm = meta[front];
        if (fm && fm.card) { fm.card.style.transform = 'none'; fm.tf = 'none'; }
      }
    }

    /* ======================================================================
       EL GIRO. Ya no lo mueve el scroll: lo mueven los gestos, y siempre
       termina en una ficha exacta.
       =================================================================== */
    function killRot() { if (rotTw) { rotTw.kill(); rotTw = null; } }

    /* La posicion es CICLICA: sumarle o restarle N fichas (360 grados) se ve
       exactamente igual. Se reduce al rango [0, N) cuando el anillo esta
       quieto y encajado, para que el angulo no crezca sin limite en una
       sesion larga (y con el, el error de coma flotante).                   */
    function normalizar() {
      if (rotTw || drag || railDrag || wOn) return;
      var m = P.pos % N;
      if (m < 0) m += N;
      if (m !== P.pos) { P.pos = m; }
    }

    /* Encaje: SIEMPRE a una ficha entera, nunca a medias. La duracion sale de
       lo que queda por girar, con el minimo y el maximo del editor (y hasta
       2,4 veces el maximo en un lanzamiento largo con inercia).             */
    function snapRot(target, dur, ease) {
      killRot();
      var t = Math.round(target);
      var d = Math.abs(t - P.pos);
      if (d < 0.0008) { P.pos = t; normalizar(); paint(); return; }
      var dd = dur != null ? dur
        : clamp(CFG.snapMin + d * 0.16, CFG.snapMin, CFG.snapMax * 2.4);
      rotTw = gsap.to(P, {
        pos: t, duration: dd, ease: ease || CFG.snapEase,
        onUpdate: paint,
        onComplete: function () { rotTw = null; P.pos = t; normalizar(); paint(); }
      });
    }

    /* Ir a la ficha i por el camino corto. Con el recorrido ciclico no hay
       extremos que envolver: es la diferencia mas corta y ya.               */
    function goIndex(i, dur) {
      var base = Math.round(P.pos);
      var cur = ((base % N) + N) % N;
      var d = i - cur;
      while (d > N / 2) d -= N;
      while (d < -N / 2) d += N;
      snapRot(base + d, dur != null ? dur : CFG.turnDur, CFG.turnEase);
    }

    /* Pixeles de arrastre por ficha. De lejos el anillo se ve pequenito y una
       ficha ocupa mucho menos en pantalla, asi que se afloja (0,45x) para que
       girarlo entero de lejos no pida 3.000 px de arrastre; al llegar, la
       sensibilidad es la del editor, clavada.                               */
    function ppc() {
      var base = isM() ? CFG.swipe : CFG.dragPx;
      return Math.max(14, base * (0.45 + 0.55 * clamp(S.f, 0, 1)));
    }

    /* Al soltar: se proyecta a donde llevaria la velocidad y se encaja a la
       ficha mas cercana a esa proyeccion. Un gesto decidido avanza al menos
       una ficha, aunque haya sido corto.                                    */
    function soltar(vel, pos0) {
      var proj = P.pos + vel * 0.33 * CFG.inertia;
      var t = Math.round(proj);
      var movido = P.pos - pos0;
      if (Math.abs(movido) > 0.18 && t === Math.round(pos0)) {
        t = Math.round(pos0) + (movido > 0 ? 1 : -1);
      }
      snapRot(t, null, 'power3.out');
    }

    /* ------------------------------------------- gesto horizontal: raton y dedo
       Un solo camino para los dos (Pointer Events). El vertical NO se toca
       nunca: `touch-action: pan-y` se lo deja al navegador y aqui solo se
       atiende el gesto cuando gana el eje horizontal por el umbral.         */
    function pDown(ev) {
      if (!sec.classList.contains('is-3d')) return;
      if (ev.pointerType === 'mouse' && ev.button > 0) return;
      drag = {
        id: ev.pointerId, x0: ev.clientX, y0: ev.clientY,
        pos0: P.pos, horiz: 0, t: ev.timeStamp || performance.now(),
        v: 0, movido: false, raton: ev.pointerType === 'mouse', cur: false
      };
    }

    function pMove(ev) {
      if (!drag || ev.pointerId !== drag.id) return;
      var dx = ev.clientX - drag.x0, dy = ev.clientY - drag.y0;
      if (!drag.horiz) {
        var u = ev.pointerType === 'mouse' ? UMBRAL_RATON : UMBRAL_DEDO;
        if (Math.abs(dx) < u && Math.abs(dy) < u) return;
        drag.horiz = Math.abs(dx) > Math.abs(dy) * 1.2 ? 1 : -1;
        if (drag.horiz !== 1) { drag = null; return; }   // vertical: del navegador
        killRot();
        // se reinicia el origen para que el anillo no pegue el salto del umbral
        drag.x0 = ev.clientX;
        drag.pos0 = P.pos;
        drag.t = ev.timeStamp || performance.now();
        /* 🚨 v4: `is-drag` SOLO con raton. Lo unico que hace esa clase es
           poner el cursor en «grabbing» (ht-anillo.css: dos reglas de
           `cursor`), y con el dedo no hay cursor que cambiar. Tocar una clase
           del escenario cuesta un recalculo de ~968 elementos (65 ms con CPU
           x4) justo al empezar y al acabar el arrastre, que es el momento en
           el que mas se nota un tiron. */
        if (drag.raton) { drag.cur = true; stage.classList.add('is-drag'); }
        try { stage.setPointerCapture(ev.pointerId); } catch (e) {}
      }
      if (ev.cancelable) ev.preventDefault();
      var now = ev.timeStamp || performance.now();
      var np = drag.pos0 - (ev.clientX - drag.x0) / ppc();
      var dt = now - drag.t;
      if (dt > 0) {
        var inst = (np - P.pos) / (dt / 1000);
        drag.v = drag.v ? drag.v * 0.65 + inst * 0.35 : inst;
        drag.t = now;
      }
      if (np !== P.pos) drag.movido = true;
      P.pos = np;
      paint();
    }

    function pUp(ev) {
      if (!drag || ev.pointerId !== drag.id) return;
      var d = drag;
      drag = null;
      if (d.horiz !== 1) return;
      /* se quita solo si se puso: `classList.remove` de una clase que no esta
         igualmente reescribe el atributo, y eso vuelve a invalidar */
      if (d.cur) stage.classList.remove('is-drag');
      try { stage.releasePointerCapture(ev.pointerId); } catch (e) {}
      /* si se ha arrastrado de verdad, el clic que viene detras no cuenta:
         arrastrar sobre una talla no debe anadir al carrito ni girar otra vez */
      if (Math.abs(P.pos - d.pos0) > 0.01) {
        didDrag = true;
        setTimeout(function () { didDrag = false; }, 320);
      }
      soltar(d.v, d.pos0);
    }

    /* 🚨 ESTO ES LO QUE ROMPIA LOS ARRASTRES CORTOS. Al apretar sobre la foto de
       un producto (que va dentro de un enlace) Chrome arranca su propio
       ARRASTRE NATIVO de imagen a los ~5 px, y en ese momento CORTA el flujo de
       pointer events: no llega ni un `pointermove` mas ni el `pointerup`. Como
       nuestro umbral de direccion todavia no habia decidido, el gesto se perdia
       entero. Medido: con pasos de 7,9 px el escenario recibia 1 pointermove y
       un `dragstart`, y el anillo no se movia; con pasos de 14,3 px el umbral
       llegaba antes y funcionaba. De ahi que «los arrastres largos van y los
       cortos no».
       Arreglo: en el anillo NUNCA se arrastra nada de forma nativa. No se
       condiciona a que el gesto ya sea horizontal: hay que cortarlo ANTES de
       saberlo. (Ademas, en el CSS, `-webkit-user-drag: none`.)            */
    function noDragNativo(ev) { ev.preventDefault(); }

    /* ---------------------------------------- trackpad / rueda horizontal
       `deltaX` gira el anillo; `deltaY` se deja pasar intacto (es el scroll
       de la pagina). Solo se hace preventDefault cuando manda el eje
       horizontal, y ahi hace falta: si no, Chrome navega hacia atras.      */
    function onWheel(ev) {
      if (!sec.classList.contains('is-3d')) return;
      var dxr = ev.deltaX, dyr = ev.deltaY;
      if (ev.deltaMode === 1) { dxr *= 16; dyr *= 16; }        // lineas -> px
      else if (ev.deltaMode === 2) { dxr *= stage.clientWidth; dyr *= window.innerHeight; }
      var ax = dxr < 0 ? -dxr : dxr, ay = dyr < 0 ? -dyr : dyr;
      if (ax < 2 || ax <= ay) return;        // vertical: no es nuestro
      ev.preventDefault();
      if (!wOn) { wOn = true; wPos0 = P.pos; killRot(); }
      P.pos += dxr / (ppc() * CFG.wheelK);
      paint();
      if (wT) clearTimeout(wT);
      wT = setTimeout(function () {
        wT = null; wOn = false;
        var movido = P.pos - wPos0;
        var t = Math.round(P.pos);
        if (Math.abs(movido) > 0.18 && t === Math.round(wPos0)) {
          t = Math.round(wPos0) + (movido > 0 ? 1 : -1);
        }
        snapRot(t, null, 'power2.out');
      }, 110);
    }

    /* ------------------------------------------- barra: arrastrar y teclado
       CAMBIO de la v3: el cuadrado gira el ANILLO directamente. En la v2
       movia el scroll de la pagina, porque el scroll era quien giraba; ahora
       el scroll solo hace el acercamiento, asi que arrastrar la barra tenia
       que dejar de tocarlo. La caja del rail se mide UNA vez al empezar a
       arrastrar, no en cada pointermove.                                    */
    function pFromX(x) {
      if (!railBox || railBox.w <= 0) return 0;
      return clamp((x - railBox.left - sqW / 2) / railBox.w, 0, 1);
    }

    function railMove(ev) {
      /* se elige la vuelta mas cercana a donde esta el anillo, para que
         agarrar el cuadrado no provoque un giro de media vuelta */
      var cand = pFromX(ev.clientX) * N;
      var rev = Math.round((P.pos - cand) / N);
      P.pos = cand + rev * N;
      paint();
    }

    function dragStart(ev) {
      if (ev.button > 0) return;
      var r = rail.getBoundingClientRect();
      railBox = { left: r.left, w: Math.max(0, r.width - sqW) };
      railDrag = true;
      killRot();
      /* igual que en el escenario: la clase solo pinta el cursor (el
         will-change del cuadrado ya lo pone setWC en linea) */
      if (ev.pointerType === 'mouse') { railCur = true; rail.classList.add('is-drag'); }
      try { rail.setPointerCapture(ev.pointerId); } catch (e) {}
      railMove(ev);
      ev.preventDefault();
    }

    function dragGuard(ev) { if (railDrag) { railMove(ev); ev.preventDefault(); } }

    function dragEnd(ev) {
      if (!railDrag) return;
      railDrag = false;
      railBox = null;
      if (railCur) { railCur = false; rail.classList.remove('is-drag'); }
      try { rail.releasePointerCapture(ev.pointerId); } catch (e) {}
      // al soltar, a la ficha mas cercana: nunca se queda entre dos
      snapRot(Math.round(P.pos), null, 'power2.out');
    }

    function railKey(ev) {
      var k = front, hit = true;
      switch (ev.key) {
        case 'ArrowRight': case 'ArrowUp': k = front + 1; break;
        case 'ArrowLeft': case 'ArrowDown': k = front - 1; break;
        case 'PageUp': k = front + 3; break;
        case 'PageDown': k = front - 3; break;
        case 'Home': k = 0; break;
        case 'End': k = N - 1; break;
        default: hit = false;
      }
      if (!hit) return;
      ev.preventDefault();
      goIndex(((k % N) + N) % N);
    }

    /* ------------------------------------- quien hay debajo del puntero
       Chrome NO acierta el hit-test de las fichas laterales: estan dentro de
       un `preserve-3d` anidado (escenario con perspectiva > camara > aro >
       hueco) y, sobre cualquier punto de una lateral, `elementFromPoint`
       devuelve siempre el contenedor `.ha__cam`, nunca la ficha. Medido: un
       barrido de 11 puntos a lo ancho de una lateral da `.ha__cam` en los 11,
       y da igual quitarle el `overflow`, la perspectiva, el `backface` o el
       transform de la camara. La de FRENTE si acierta (ahi caen la foto, las
       tallas y el boton de anadir, y por eso la compra funciona), porque
       acaba en z = 0 exacto.

       Asi que las laterales se buscan a mano, y sale exacto: la ficha se
       desgira justo lo que la ha girado el aro, o sea queda PARALELA a la
       pantalla, con lo que su caja proyectada es un rectangulo recto y
       `getBoundingClientRect()` da el rectangulo que de verdad se ve. Se
       recorre de la mas cercana al frente a la mas lejana (que es el orden
       en el que se dibujan unas sobre otras), asi un punto donde dos fichas
       se solapan cae en la de delante, como se ve.                          */
    function slotAt(x, y) {
      var pos = P.pos, best = null, bestA = 1e9;
      for (var i = 0; i < N; i++) {
        if (hidCache[i] || !meta[i].card) continue;
        var off = i - pos;
        while (off > N / 2) off -= N;
        while (off < -N / 2) off += N;
        var a = off < 0 ? -off : off;
        if (a >= bestA) continue;              // ya hay una mas al frente
        var r = meta[i].card.getBoundingClientRect();
        if (x < r.left || x > r.right || y < r.top || y > r.bottom) continue;
        best = meta[i].slot; bestA = a;
      }
      return best;
    }

    /* El cursor de mano sobre las laterales. Solo se calcula con el anillo
       QUIETO: leer rectangulos mientras se mueve forzaria un reflow por cada
       movimiento del raton, y eso es justo lo que no se quiere.             */
    var aimOn = false;
    function setAim(on) {
      if (on === aimOn) return;
      aimOn = on;
      stage.classList.toggle('is-aim', on);
    }
    function aim(ev) {
      if (ev.pointerType && ev.pointerType !== 'mouse') return;
      if (drag || !still || S.f < 0.5) { setAim(false); return; }
      var s = slotAt(ev.clientX, ev.clientY);
      setAim(!!s && !s.classList.contains('is-front'));
    }
    function aimOut() { setAim(false); }

    /* -------------------------------------------- clic en una ficha lateral */
    /* 🚨 CLIC PERDIDO EN LA TALLA. Chrome NO siempre hace hit-test de las
       fichas del aro: esta apuntado en el CSS (es la razon de `slotAt` y de la
       regla `is-aim` para el cursor de mano). Cuando falla, un clic sobre la
       TALLA de la ficha de FRENTE llega con `target` = `.ha__cam`, no encuentra
       ficha, `slotAt` dice que es la de frente y el manejador se iba sin hacer
       nada: el usuario pincha la talla y no pasa nada -medido en el banco a
       1920x1080, con la ficha quieta, encajada y con pointer-events: auto, 5
       intentos seguidos devolvian DIV.ha__cam-. Las CAJAS si son correctas
       (`getBoundingClientRect`), asi que el clic se reenvia a mano al control
       cuyo rectangulo contiene el punto. Solo se entra aqui cuando el navegador
       YA ha fallado: si acierta, el clic lo gestiona el y esto no corre.     */
    /* 9 oct 2026 - Diego: «que al pulsar la FOTO del producto tambien lleve a
       el, no solo el titulo». Clic en la foto de la ficha de FRENTE = ir a la
       ficha de producto (el mismo enlace que el titulo). Se mira el rectangulo
       de la foto, no el `target`, porque Chrome no siempre acierta el hit-test
       del aro (ver `slotAt`). Solo con la camara ya encima (de lejos un toque
       es para girar, no para comprar) y nunca sobre un control (tallas,
       boton, enlaces). Ctrl/Cmd o rueda = pestana nueva, como un enlace.   */
    function irAFicha(slot, ev) {
      if (S.f < 0.98) return false;
      if (ev.target.closest && ev.target.closest('a,button,input,select,textarea,label')) return false;
      var media = slot.querySelector('.ha-card__media');
      var a = slot.querySelector('.ha-card__name a');
      if (!media || !a || !a.href) return false;
      var r = media.getBoundingClientRect();
      if (ev.clientX < r.left || ev.clientX > r.right || ev.clientY < r.top || ev.clientY > r.bottom) return false;
      ev.preventDefault();
      if (ev.metaKey || ev.ctrlKey || ev.button === 1) window.open(a.href, '_blank');
      else window.location.href = a.href;
      return true;
    }

    function reenviarAlFrente(slot, ev) {
      var card = slot.querySelector('.ha-card');
      if (!card) return false;
      var ctr = card.querySelectorAll('.ha-size, .ha-add, .ha-card__name a');
      for (var i = 0; i < ctr.length; i++) {
        var el = ctr[i];
        if (el.disabled) continue;
        var r = el.getBoundingClientRect();
        if (ev.clientX < r.left || ev.clientX > r.right) continue;
        if (ev.clientY < r.top || ev.clientY > r.bottom) continue;
        ev.preventDefault();
        ev.stopPropagation();
        /* el clic sintetico vuelve a subir hasta este mismo manejador, pero ya
           con `target` dentro de la ficha: entra por el camino normal y se va
           sin tocar nada. No hay bucle. */
        el.click();
        return true;
      }
      return false;
    }

    function onClick(ev) {
      if (didDrag) {                      // venia de un arrastre: no es un clic
        didDrag = false;
        ev.preventDefault();
        ev.stopPropagation();
        return;
      }
      var slot = ev.target.closest && ev.target.closest('.ha__slot');
      var perdido = false;
      if (!slot && sec.classList.contains('is-3d')) {
        slot = slotAt(ev.clientX, ev.clientY);
        perdido = true;        // el clic no ha caido en ninguna ficha
      }
      if (!slot) return;
      if (slot.classList.contains('is-front')) {
        if (perdido && reenviarAlFrente(slot, ev)) return;
        irAFicha(slot, ev);
        return;
      }
      ev.preventDefault();
      ev.stopPropagation();
      var i = slots.indexOf(slot);
      if (i < 0) return;
      goIndex(i);
    }

    /* ---------------------------------------------- montaje de la timeline
       UN solo tramo: el acercamiento. Detras, un tramo quieto (`hold`) para
       que al llegar el anillo se quede un rato centrado antes de soltar el
       pin. Sin snap: el scroll del usuario no se toca nunca.                */
    sec.classList.add('is-3d', 'is-pinned');
    meta.forEach(function (m) { if (m.turn) m.turn.disabled = false; });
    measure();

    /* ---------------------------------------- v4: el acercamiento, con muelle
       El scroll marca un OBJETIVO (0..1 del tramo de acercamiento) y un
       muelle criticamente amortiguado lleva hasta el. Se integra con la
       solucion EXACTA (no paso a paso), asi que da igual que un fotograma
       tarde 8 o 40 ms: la curva es la misma y nunca se pasa ni rebota.
         x(t) = (x0 + (v0 + w x0) t) e^(-w t)
       «Suavizado del acercamiento» (scrub) sigue mandando: es el tiempo que
       tarda en alcanzar el scroll (95 % a los `scrub` segundos). La curva del
       editor se aplica encima, sobre la posicion del muelle.               */
    var zEase = (gsap.parseEase && gsap.parseEase(CFG.zoomEase)) || function (x) { return x; };
    var Z = { p: 0, v: 0, tgt: 0, on: false };
    var OMEGA = 4.74 / Math.max(0.08, CFG.scrub);

    function zApply() {
      S.f = Z.p >= 1 ? 1 : (Z.p <= 0 ? 0 : zEase(Z.p));
      paint();
    }
    function zStop() { if (Z.on) { Z.on = false; gsap.ticker.remove(zTick); } }
    function zTick(time, dtMs) {
      var dt = Math.min(0.064, Math.max(0.001, (dtMs || 16.7) / 1000));
      var x = Z.p - Z.tgt, e = Math.exp(-OMEGA * dt), c = Z.v + OMEGA * x;
      var nx = (x + c * dt) * e;
      Z.v = (Z.v - OMEGA * c * dt) * e;
      Z.p = Z.tgt + nx;
      if (Math.abs(nx) < 0.0003 && Math.abs(Z.v) < 0.003) { Z.p = Z.tgt; Z.v = 0; zStop(); }
      zApply();
    }
    function zSet(prog, ya) {
      Z.tgt = clamp(prog / ZF, 0, 1);
      /* 🚨 EN TACTIL, SIN MUELLE. El muelle esta para que la RUEDA no de
         arrancones: cada golpe de rueda es un salto de velocidad. Con el dedo
         no hay nada que suavizar -el compositor ya entrega el scroll
         interpolado fotograma a fotograma- y el suavizado solo resta: medido
         en iPhone 13, con un flick el pin de 1.000 px se cruza en 48
         fotogramas y el muelle se quedaba en f = 0,82 al soltarse el pin, o
         sea que el acercamiento TERMINABA EN EL AIRE, con la seccion ya
         moviendose, y hacian falta 344 px mas de scroll para llegar a 1. La v3
         (con el `scrub` de ScrollTrigger) llegaba a 1 siempre. */
      if (ya || CFG.scrub <= 0 || tactil()) { Z.p = Z.tgt; Z.v = 0; zStop(); zApply(); return; }
      if (!Z.on && Z.p !== Z.tgt) { Z.on = true; gsap.ticker.add(zTick); }
    }

    var startStr = 'top top';
    if (CFG.pinDy > 0) startStr = 'top top+=' + CFG.pinDy;
    else if (CFG.pinDy < 0) startStr = 'top top-=' + (-CFG.pinDy);

    var cfgSt = {
      trigger: sec,
      start: startStr,
      end: '+=' + TOT,
      pin: true,
      pinSpacing: true,
      /* engancha el pin un pelin antes cuando el scroll va rapido: sin esto,
         con un golpe fuerte de rueda la seccion se pasaba un fotograma de
         largo antes de quedarse fija (el scroll va en otro hilo) */
      /* 🚨 `anticipatePin` engancha el pin un pelin ANTES cuando el scroll va
         rapido. En PC con la rueda eso quita un fotograma en el que la seccion
         se pasaba de largo. En MOVIL hace justo el destrozo que vio Diego: con
         un flick la velocidad es enorme (un gesto normal recorre 2.200-4.500 px)
         y ScrollTrigger ancla la seccion 130 px ANTES de su sitio. Medido en
         iPhone 13 con dedo de verdad: la seccion pega un salto de 200 px en UN
         fotograma y se monta 90 px ENCIMA del hero; al soltar el pin, otro de
         203-244 px hacia abajo que esconde el anillo -de ahi el «hay que bajar
         mas para ver los productos»-. Con 0: 4-5 px. */
      anticipatePin: tactil() ? 0 : 1,
      onUpdate: function (self) { zSet(self.progress); },
      onRefreshInit: measure,
      /* tras medir, la camara se planta donde diga el scroll, sin muelle */
      onRefresh: function (self) { zSet(self.progress, true); },
      /* al salir del pin, por abajo o por arriba, se hace el refresh que haya
         quedado pendiente (ver `refreshPend`). */
      onLeave: function () { refreshPend(); },
      onLeaveBack: function () { refreshPend(); }
    };
    if (CFG.zoomSnap) {
      /* apagado por defecto. Es lo unico que puede volver a escribir el scroll
         del usuario, y con el pin tan corto no hace falta. */
      cfgSt.snap = {
        snapTo: CFG.hold > 0 ? [0, ZF, 1] : [0, 1],
        duration: { min: CFG.snapMin, max: CFG.snapMax },
        delay: 0.09, ease: CFG.snapEase, directional: true, inertia: false
      };
    }
    st = ScrollTrigger.create(cfgSt);

    paint();

    /* ------------------------------------------------------------ escuchas */
    if (rail) {
      rail.addEventListener('pointerdown', dragStart);
      rail.addEventListener('pointermove', dragGuard);
      rail.addEventListener('pointerup', dragEnd);
      rail.addEventListener('pointercancel', dragEnd);
      rail.addEventListener('keydown', railKey);
    }
    stage.addEventListener('pointerdown', pDown, { passive: true });
    stage.addEventListener('pointermove', pMove);
    stage.addEventListener('pointermove', aim, { passive: true });
    stage.addEventListener('pointerleave', aimOut, { passive: true });
    stage.addEventListener('pointerup', pUp, { passive: true });
    stage.addEventListener('pointercancel', pUp, { passive: true });
    stage.addEventListener('dragstart', noDragNativo);
    stage.addEventListener('click', onClick, true);
    sec.addEventListener('wheel', onWheel, { passive: false });

    /* -------------------------- refrescos: solo cuando de verdad hacen falta
       ScrollTrigger ya refresca solo en un resize de ventana. Aqui solo se
       cubre lo que cambia el alto del contenido despues de montar: que
       lleguen las tipografias. Todo junto en un unico refresh con freno.

       🚨 3 oct 2026 - UN REFRESH DENTRO DEL PIN ROMPE EL ANCLAJE.
       Medido en movil 390x844: tres fotos del aro (van `lazy`, asi que
       terminan de cargar justo cuando la seccion llega a la pantalla, no
       antes) disparaban un `ScrollTrigger.refresh()` en el mismo fotograma en
       el que el pin se engancha. GSAP volvia a medir con la seccion YA
       desplazada por el pin -en movil el pin va por `transform`- y el `start`
       pasaba de 2.234 a -20 px: el acercamiento se saltaba entero (f = 1 de
       golpe) y la seccion daba un salto de 1.000 px hacia abajo. Dos cambios:
         1) las fotos del aro YA NO refrescan. No hace falta: la ficha tiene
            ancho y alto puestos por CSS y el `img` lleva width/height, asi
            que cargar la foto no mueve ni un pixel del layout.
         2) cualquier refresh nuestro ESPERA a que el pin no este activo: si
            llega dentro del pin se queda pendiente y se hace al salir.      */
    var refPend = false;

    function refreshNow() {
      if (st && st.isActive) { refPend = true; return; }
      refPend = false;
      ScrollTrigger.refresh();
    }

    function refreshSoon() {
      if (refreshT) clearTimeout(refreshT);
      refreshT = setTimeout(function () { refreshT = null; refreshNow(); }, 220);
    }

    /* lo llaman `onLeave` y `onLeaveBack` del ScrollTrigger: al salir del pin
       se hace el refresh que se quedo pendiente. Con un hueco de un par de
       fotogramas, porque dentro del callback ScrollTrigger aun se esta
       actualizando a si mismo.                                              */
    function refreshPend() {
      if (!refPend) return;
      refPend = false;
      setTimeout(function () { ScrollTrigger.refresh(); }, 60);
    }

    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refreshSoon);

    /* Red de seguridad por si el contenido cambia de alto por otra via. Con
       freno, comparando contra el alto que acabamos de escribir nosotros y
       sin entrar nunca dentro del pin: sin esas tres cosas (la v1 no tenia
       ninguna) cada medida disparaba otro refresh, y un refresh dentro del
       pin da un salto.                                                      */
    /* ----------------------------------------- vigilante del anclaje (12 oct)
       🚨 El `start` del pin se calcula UNA vez y solo se rehace si cambia el
       escenario del propio anillo. Si cambia de alto cualquier cosa ENCIMA
       (el hero va en `svh`; la barra del navegador, fotos o tipografias que
       llegan tarde), el anclaje se queda viejo y la diferencia es exactamente
       el salto que se ve al entrar y al salir: medido en movil con dedo,
       start 1.990 con la seccion en 2.234 -> brinco de 244 px en un fotograma
       al anclarse y otro igual al soltarse (estaba asi desde el 9 oct).
       Aqui, mientras se hace scroll CERCA del anillo y FUERA del pin, se
       compara donde esta de verdad su hueco con el `start` que tiene GSAP; si
       no cuadran, refresh (fuera del pin es seguro). Una lectura de layout por
       fotograma y solo en esa zona. No reintenta con la misma diferencia, para
       que una medida que no cuadre por otra razon no entre en bucle.         */
    var gRaf = 0, gUlt = null;
    function guardia() {
      gRaf = 0;
      if (!st || st.isActive) return;
      var y = window.pageYOffset, vh = window.innerHeight;
      if (y < st.start - vh * 3 || y > st.end + vh * 2) return;
      var ref = sec.parentNode && sec.parentNode.classList && sec.parentNode.classList.contains('pin-spacer') ? sec.parentNode : sec;
      var dif = Math.round(ref.getBoundingClientRect().top + y - CFG.pinDy - st.start);
      if (Math.abs(dif) <= 3) { gUlt = null; return; }
      if (dif === gUlt) return;
      gUlt = dif;
      refreshNow();
    }
    function onScrollGuardia() { if (!gRaf) gRaf = requestAnimationFrame(guardia); }
    window.addEventListener('scroll', onScrollGuardia, { passive: true });

    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(function (e) {
        var h = Math.round(e[0].contentRect.height);
        if (Math.abs(h - roH) < 4) return;
        roH = h;
        if (roT) clearTimeout(roT);
        roT = setTimeout(function () { roT = null; refreshNow(); }, 280);
      });
      ro.observe(stage);
    }

    return {
      el: sec,
      // ventana al estado interno: la usa el banco de pruebas para medir el
      // pin sin adivinar (y va bien para depurar desde la consola)
      info: function () {
        return {
          start: st ? st.start : 0, end: st ? st.end : 0,
          progress: st ? st.progress : 0, active: st ? st.isActive : false,
          zoomPx: CFG.zoom, holdPx: CFG.hold, total: TOT, zoomSnap: CFG.zoomSnap,
          far: FAR, farPC: CFG.far, farM: CFG.farM, tilt: CFG.tilt, camy: CFG.camy,
          zFrac: ZF, stepDeg: STEP_DEG,
          scrub: CFG.scrub, veil: CFG.veil, draw: CFG.draw,
          dragPx: CFG.dragPx, swipe: CFG.swipe, ppc: +ppc().toFixed(1),
          wheelK: CFG.wheelK, inertia: CFG.inertia, turnDur: CFG.turnDur,
          snapMin: CFG.snapMin, snapMax: CFG.snapMax,
          n: N, sideMax: SIDE_MAX, side: perSide(),
          front: front, R: R, cardW: cardW,
          f: +S.f.toFixed(4), pos: +P.pos.toFixed(4),
          rot: +(-P.pos * STEP_DEG).toFixed(3),
          girando: !!rotTw, arrastrando: !!drag,
          still: still, rest: rest,
          ocultas: hidCache.filter(Boolean).length
        };
      },
      // para el banco: lleva el anillo a una ficha al instante
      saltar: function (k) { killRot(); P.pos = k; paint(); },
      // para el banco: gira d fichas con el mismo camino que un clic
      girar: function (d) { snapRot(Math.round(P.pos) + d, CFG.turnDur, CFG.turnEase); },
      destroy: function () {
        if (rail) {
          rail.removeEventListener('pointerdown', dragStart);
          rail.removeEventListener('pointermove', dragGuard);
          rail.removeEventListener('pointerup', dragEnd);
          rail.removeEventListener('pointercancel', dragEnd);
          rail.removeEventListener('keydown', railKey);
        }
        stage.removeEventListener('pointerdown', pDown);
        stage.removeEventListener('pointermove', pMove);
        stage.removeEventListener('pointermove', aim);
        stage.removeEventListener('pointerleave', aimOut);
        stage.removeEventListener('pointerup', pUp);
        stage.removeEventListener('pointercancel', pUp);
        stage.removeEventListener('dragstart', noDragNativo);
        stage.removeEventListener('click', onClick, true);
        sec.removeEventListener('wheel', onWheel);
        if (ro) ro.disconnect();
        if (roT) clearTimeout(roT);
        window.removeEventListener('scroll', onScrollGuardia);
        if (gRaf) cancelAnimationFrame(gRaf);
        if (refreshT) clearTimeout(refreshT);
        if (stillT) clearTimeout(stillT);
        if (wT) clearTimeout(wT);
        zStop();
        killRot();
        if (st) { st.kill(true); st = null; }
        if (tl) { tl.kill(); tl = null; }
        sec.classList.remove('is-3d', 'is-pinned', 'is-still', 'is-rest');
        ['--ha-cw', '--ha-sh', '--ha-buy-h', '--ha-R', '--ha-slot-left',
         '--ha-camz', '--ha-tilt', '--ha-camy'].forEach(function (v) { sec.style.removeProperty(v); });
        ring.style.removeProperty('--ha-rot');
        ring.style.transform = '';
        cam.style.transform = '';
        ring.style.willChange = ''; cam.style.willChange = '';
        if (sq) sq.style.willChange = '';
        if (sq) sq.style.removeProperty('--ha-sq-x');
        stage.classList.remove('is-aim', 'is-drag');
        meta.forEach(function (m) {
          m.slot.classList.remove('is-front', 'is-hid');
          m.slot.style.cssText = '';
          if (m.card) { m.card.removeAttribute('aria-hidden'); m.card.style.transform = ''; }
          if (m.veil) m.veil.style.opacity = '';
          m.foc.forEach(function (el) { el.removeAttribute('tabindex'); });
          if (m.turn) m.turn.disabled = true;
        });
      }
    };
  }

  /* ========================================================================
     Compra rapida: fija la variante elegida antes de que el formulario salga.
     Usa el <product-form-component> del propio tema, asi el cajon del carrito
     y el contador se comportan igual que en la ficha de producto.
     ===================================================================== */
  function compra() {
    document.addEventListener('click', function (ev) {
      var b = ev.target.closest && ev.target.closest('[data-ha-variant]');
      if (!b || b.disabled) return;
      var slot = b.closest('.ha__slot');
      if (slot && !slot.classList.contains('is-front') && slot.closest('.ha.is-3d')) return;
      var form = b.closest('form');
      var input = form && form.querySelector('[data-ha-variant-input]');
      if (input) input.value = b.getAttribute('data-ha-variant');
      b.classList.add('is-done');
      setTimeout(function () { b.classList.remove('is-done'); }, 1200);
    }, true);
  }

  /* ---------------------------------------------------------------- arranque */
  function mount(root) {
    if (!can3d()) return;
    gsap.registerPlugin(ScrollTrigger);
    /* en movil la barra de direcciones del navegador cambia el alto del
       viewport al desplazarse: sin esto, cada aparicion dispara un refresh
       general y el anillo pega un salto en mitad del gesto */
    ScrollTrigger.config({ ignoreMobileResize: true });
    /* 🚨 12 oct 2026 — EL SCROLL SUAVE DEL TEMA ROMPIA CADA REFRESH.
       `base.css` pone `scroll-behavior: smooth` en <html>. Durante un refresh
       ScrollTrigger lleva el scroll a 0 para medir y luego lo devuelve; con el
       scroll suave ese salto se ANIMA en vez de ocurrir, GSAP mide con la
       pagina aun desplazada y el `start` sale descolocado exactamente lo que
       estabas desplazado (medido en PC: 1.200 px de scroll -> start 1.179 en
       vez de 2.379). Pasaba en uso normal (cambiar el tamano de la ventana,
       tipografias que llegan tarde, el refresh pendiente al salir del pin) y
       por eso el salto al entrar/salir del anillo iba y venia. Solo mientras
       dura el refresh se apaga el scroll suave; al terminar se devuelve. */
    if (!window.__haSB) {
      window.__haSB = 1;
      var html = document.documentElement;
      ScrollTrigger.addEventListener('refreshInit', function () { html.style.scrollBehavior = 'auto'; });
      ScrollTrigger.addEventListener('refresh', function () { html.style.scrollBehavior = ''; });
    }
    Array.prototype.slice.call((root || document).querySelectorAll('.ha')).forEach(function (sec) {
      if (sec.__ha) return;
      var inst = Anillo(sec);
      if (inst) { sec.__ha = inst; instances.push(inst); }
    });
  }

  function unmount(root) {
    instances = instances.filter(function (i) {
      if (root && !root.contains(i.el)) return true;
      i.el.__ha = null;
      i.destroy();
      return false;
    });
  }

  /* Las fotos del aro van `lazy` para no competir con la portada, pero de
     lejos se ven LAS 20 a la vez: si alguna llega tarde, aparece de golpe. En
     cuanto la pagina ha cargado y queda en reposo, se piden todas (son
     ~20 fotos de 300-400 px). */
  function precargar() {
    var go = function () {
      Array.prototype.forEach.call(document.querySelectorAll('.ha img[loading="lazy"]'), function (im) {
        im.loading = 'eager';
      });
    };
    var idle = function () {
      if (window.requestIdleCallback) requestIdleCallback(go, { timeout: 2500 });
      else setTimeout(go, 600);
    };
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }

  function start() {
    compra();
    precargar();
    mount(document);
    // el editor del tema recarga la seccion sola: hay que volver a montarla
    document.addEventListener('shopify:section:load', function (e) {
      if (e.target.querySelector && e.target.querySelector('.ha')) {
        unmount(e.target);
        mount(e.target);
        if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
      }
    });
    document.addEventListener('shopify:section:unload', function (e) { unmount(e.target); });
    RM.addEventListener && RM.addEventListener('change', function (e) {
      if (e.matches) unmount(null); else mount(document);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
